import * as auth from './utils/auth.js';
import * as ui from './utils/ui.js';
import * as events from './utils/events.js';
import * as action from './utils/action.js';
import * as player from './utils/player.js';

export default class Sonikit extends EventTarget {
  constructor() {
    super();

    this.eventQueue = [];
    this.events = {};

    this.base_url = this.getBaseURL();
    this.auth_callback = `${this.base_url}/audio-auth`;
    this.initialized = false;
    this.apiKey = null;

    this.device = window.matchMedia("(pointer: coarse)").matches ? 'mobile' : 'desktop';
    this.status = 'loading';
    this.storage = this.getStoredData();
  }

  getBaseURL() {
    return window.location.href.includes('localhost:3000') 
      ? 'https://wayward.ngrok.io' 
      : 'https://api.sonikit.com';
  }

  getStoredData() {
    const storage = localStorage.getItem('sonikit-store');
    return storage ? JSON.parse(storage) : { fan: null, patch: null, service: null, contact_id: null, contact_token: null };
  }

  async init({ key }) {
    if (this.initialized) return;
    if (!key) return this.logError("Missing API key for initialization.");

    this.apiKey = key;
    this.initialized = true;

    const scriptSrc = document.currentScript?.src || import.meta.url;
    const params = new URLSearchParams(new URL(scriptSrc).search);
    this.page_id = params.get('page') || null;
    this.station_id = params.get('station') || null;

    const urlParams = new URLSearchParams(window.location.search);
    this.fan_id = urlParams.get('fan') || this.storage.fan || null;
    this.patch_id = urlParams.get('patch_id') || null;
    this.service_id = urlParams.get('service_id') || null;
    window.history.replaceState(null, '', window.location.origin + window.location.pathname);

    this.emit('initialized');
    this.log("Sonikit initialized with key:", key);

    this.eventQueue.forEach(({ event, callback }) => {
      this._registerEvent(event, callback);
    });

    this.eventQueue = [];

    await this.configure(this);
  }

  async configure() {
    try {
      await this.fetchProject();
      if (!this.project) throw new Error("Invalid project");

      events.init(this);
      if (this.page_id) {
        events.create({ category: 'page-view', page_id: this.page_id });
      } else if (this.station_id) {
        events.create({ category: 'page-view', station_id: this.station_id });
      }

      await this.getLocation();

      auth.init(this);
      action.init(this);

      if (!this.page_id) {
        ui.init(this);
      }

      this.syncFanSession();

      this.status = 'ready';
      this.emit('ready');
    } catch (error) {
      this.logError("Configuration failed:", error);
    }
  }

  async fetchProject() {
    const role = this.page_id ? 'page_element' : this.station_id ? 'station_element' : 'default';
    const response = await this.fetchFromAPI(`/setup?role=${role}`);

    if (response) {
      this.project = response.project;
      this.apple_token = response.appleDevToken;

      this.player_patch = this.station_id 
        ? this.project.patches.find(x => x.station === this.station_id)
        : this.project.patches.find(x => x.action === 'player' && x.role === 'default');
    }
  }

  async getLocation() {
    const data = await this.fetchFromAPI('/location');
    if (data && data.ip !== 'undefined') {
      this.location = data;
    }
  }

  async fetchFromAPI(endpoint, options = {}) {
    try {
      const response = await fetch(`${this.base_url}${endpoint}`, {
        method: options.method || 'GET',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json'
        },
        ...options
      });

      if (!response.ok) throw new Error(`API error: ${response.statusText}`);
      return response.json();
    } catch (error) {
      this.logError(`Failed to fetch ${endpoint}:`, error);
      return null;
    }
  }

  async syncFanSession() {
    if (this.fan_id) await this.setFan(this.fan_id);

    if (this.fan_id && this.patch_id && this.service_id) {
      this.handleCallback(this.patch_id, this.service_id);
    } else if (this.storage.fan && this.storage.service && this.player_patch) {
      const service = this.player_patch.services.find(x => x.name === this.storage.service);
      if (service) action.execute(this.player_patch, service);
    }
  }

  async setFan(fanId) {
    const user = await auth.getUser(null, fanId, null);
    if (!user || user.error) return console.log('User error');

    this.user = user;
    this.storage.fan = this.user._id;
    this.storage.service = user.service;
    localStorage.setItem('sonikit-store', JSON.stringify(this.storage));
    // Save an array of fan auth based on service.
    // If user signs in first with Spotify, then Amazon, save both sets of data so they can switch between them

    this.emit('authenticated', this.user );
    return user;
  }

  handleCallback(patch_id, service_id) {
    const patch = this.project.patches.find(x => x.id === patch_id);
    const service = patch.services.find(x => x.id === service_id);
    
    if (this.player_patch && this.player_patch.id === patch.id) {
      action.execute(patch, service);
    } else {
      if (service.name === 'apple-music') {
        action.execute(patch, service);
      } else {
        ui.showConfirmation(patch, service);
        action.processAdapters(patch, service);
      }
    }
  }

  triggerAction(patch_id, media_id, media_type) {
    const patch = this.project.patches.find(x => x.id === patch_id);
    const service = patch.services.find(x => x.name === this.user.service);
    if (media_id && media_id !== service.media_id) {
      service.media_id = media_id;
      service.media_tpe = media_type;
    }
    action.execute(patch, service, media_id, media_type);
  }

  authenticate(patch_id, service_id, media_id, media_type) {
    const patch = this.project.patches.find(x => x.id === patch_id)
    if (!patch) return console.log('Patch not found (authenticate)')
    const service = patch.services.find(x => x.id === service_id);
    if (!service) return console.log('Service not found (authenticate)')
    auth.trigger(patch, service, media_id, media_type);
  }

  startBackupPlayer() {
    const patch = this.project.patches.find(x => x.action === 'player');
    if (!patch) return console.log('no patch');
    const service = patch.services.find(x => x.name === 'youtube');
    if (!service) return console.log('no service');
    player.init(this, patch, service);
  } 

  clearSessions() {
    localStorage.removeItem('sonikit-store');
    if (window.MusicKit) MusicKit.getInstance().unauthorize();
    if (this.player) this.player.reset();
  }

  log(...args) {
    console.log("[Sonikit]", ...args);
  }

  logError(...args) {
    console.error("[Sonikit Error]", ...args);
  }

  on(event, callback) {
    if (this.initialized) {
      this._registerEvent(event, callback);
    } else {
      this.eventQueue.push({ event, callback });
    }
  }

  _registerEvent(event, callback) {
    if (!this.events[event]) this.events[event] = [];
    this.events[event].push(callback);
  }

  off(eventName, listener) {
    this.removeEventListener(eventName, listener);
  }

  emit(event, data) {
    if (this.events[event]) {
      this.events[event].forEach(callback => callback(data));
    }
  }
}

window.sonikit = new Sonikit();
window.dispatchEvent(new Event("sonikitLoaded"));

// const scriptSrc = document.currentScript?.src || import.meta.url;
// const params = new URLSearchParams(new URL(scriptSrc).search);
// const key = params.get('key') || null;

const thisScriptName = import.meta.url.split('/').pop().split('?')[0]; // embed.js

let scriptTag;
for (const script of document.querySelectorAll('script')) {
  const scriptName = script.src?.split('/').pop()?.split('?')[0];
  if (scriptName === thisScriptName) {
    scriptTag = script;
    break;
  }
}

let key = null;
if (scriptTag) {
  const url = new URL(scriptTag.src);
  key = url.searchParams.get('key');
}

if (!window.sonikit.initialized && key) {
  window.sonikit.init({ key });
}